import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHeadset, FaTimes, FaPaperPlane, FaEnvelope, 
  FaCrown, FaQuestionCircle, FaBug, FaLightbulb
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { contactAPI } from '../../services/api';
import { toast } from 'react-toastify';

const SUPPORT_EMAIL = 'support@appozy.com';

const SupportWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    category: 'question',
    subject: '',
    message: '',
    guestEmail: '',
    guestName: ''
  });

  // Mutual exclusion with other floating panels (AIChatWidget,
  // GlobalNotesWidget) — opening this one closes the others.
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('appozy:floating-panel-opened', {
        detail: { id: 'support' }
      }));
    }
    const onOtherOpen = (e) => {
      if (e.detail?.id !== 'support') setIsOpen(false);
    };
    window.addEventListener('appozy:floating-panel-opened', onOtherOpen);
    return () => window.removeEventListener('appozy:floating-panel-opened', onOtherOpen);
  }, [isOpen]);

  const isPriority = ['pro', 'business', 'enterprise'].includes(user?.subscription?.plan);
  const isPaid = ['starter', 'pro', 'business', 'enterprise'].includes(user?.subscription?.plan);

  const categories = [
    { id: 'question', label: 'Question', icon: FaQuestionCircle, color: 'text-blue-500' },
    { id: 'bug', label: 'Bug Report', icon: FaBug, color: 'text-red-500' },
    { id: 'feature', label: 'Feature Request', icon: FaLightbulb, color: 'text-amber-500' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    // Guest flow requires email + name
    if (!user && (!form.guestEmail.trim() || !form.guestName.trim())) {
      toast.error('Please provide your name and email');
      return;
    }

    setSending(true);

    try {
      // Logged-in users → /contact/issue endpoint, which auto-captures
      // user identity from the auth token plus the page URL and User-Agent
      // so bug reports come pre-loaded with debugging context. Generous
      // 10/hr per-user rate limit so a debugging session doesn't lock out.
      // Logged-out visitors → public /contact form, 3/hr per IP.
      //
      // Subject + description + category + priority go as separate
      // fields — previously we built one multi-line description string
      // and the backend used the first 60 chars as the email subject,
      // which embedded literal `\n` characters and Resend rejected the
      // request with a 422 validation_error.
      if (user) {
        await contactAPI.reportIssue({
          subject: form.subject,
          description: form.message,
          category: form.category,
          priority: isPriority ? 'HIGH' : 'Normal',
          currentUrl: window.location.href,
          userAgent: navigator.userAgent,
        });
      } else {
        await contactAPI.send({
          name: form.guestName,
          email: form.guestEmail,
          subject: `[${form.category.toUpperCase()}] ${form.subject}`,
          message: `Category: ${form.category}\nPlan: visitor (not signed in)\n\n${form.message}`,
        });
      }

      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');

      // Reset after delay
      setTimeout(() => {
        setSent(false);
        setForm({ category: 'question', subject: '', message: '', guestEmail: '', guestName: '' });
      }, 4000);

    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to send. Try emailing us directly at ${SUPPORT_EMAIL}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, delay: 1 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 ${
          isOpen
            ? 'bg-gray-800 rotate-0'
            : isPriority
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-200/50'
              : 'bg-gradient-to-r from-violet-600 to-indigo-600 shadow-violet-200/50'
        }`}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <FaTimes className="text-white text-lg" />
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <FaHeadset className="text-white text-lg" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Priority badge */}
        {isPriority && !isOpen && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center border-2 border-white">
            <FaCrown className="text-white text-[8px]" />
          </div>
        )}
      </motion.button>

      {/* Support Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-40 right-4 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-96 max-w-[24rem] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <div className={`px-6 py-4 bg-gradient-to-r ${isPriority ? 'from-amber-500 to-orange-500' : 'from-violet-600 to-indigo-600'} text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-bold text-lg">Contact Support</h3>
                    {isPriority && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-xs font-bold">
                        <FaCrown className="mr-1 text-[8px]" /> Priority
                      </span>
                    )}
                  </div>
                  <p className="text-white/70 text-sm mt-0.5">
                    {!user ? 'Have a question? We\'re here to help.' : isPriority ? 'Priority response within 4 hours' : 'We typically respond within 24 hours'}
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            {sent ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-8 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <FaPaperPlane className="text-emerald-600 text-xl" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Message Sent!</h4>
                <p className="text-sm text-gray-500">We'll get back to you {isPriority ? 'within 4 hours' : 'within 24 hours'}.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Guest fields (only when logged out) */}
                {!user && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Your Name</label>
                      <input
                        type="text"
                        value={form.guestName}
                        onChange={(e) => setForm({ ...form, guestName: e.target.value })}
                        placeholder="Jane Doe"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                      <input
                        type="email"
                        value={form.guestEmail}
                        onChange={(e) => setForm({ ...form, guestEmail: e.target.value })}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-all"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Category */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Category</label>
                  <div className="flex space-x-2">
                    {categories.map(cat => {
                      const CatIcon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setForm({ ...form, category: cat.id })}
                          className={`flex-1 flex items-center justify-center space-x-1.5 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            form.category === cat.id
                              ? 'border-violet-500 bg-violet-50 text-violet-700'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          <CatIcon className={`text-xs ${form.category === cat.id ? 'text-violet-500' : cat.color}`} />
                          <span>{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Brief description"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-all"
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Message</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={4}
                    placeholder="Tell us what's on your mind..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-all resize-none"
                    required
                  />
                </div>

                {/* Reply-to info (only for logged-in users) — shows
                    where our response will arrive in THEIR inbox.
                    Reworded from "Reply will be sent to" because that
                    read ambiguously alongside "Or email us directly at
                    support@appozy.com" below. */}
                {user && (
                  <div className="bg-gray-50 rounded-xl p-3 flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <FaEnvelope className="text-violet-500 text-xs" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">We'll reply to your inbox at</p>
                      <p className="text-sm font-medium text-gray-700 truncate">{user?.email}</p>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={sending}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`w-full py-3 rounded-xl font-semibold text-sm text-white flex items-center justify-center space-x-2 transition-all ${
                    isPriority
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg shadow-amber-200/50'
                      : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:shadow-lg shadow-violet-200/50'
                  }`}
                >
                  {sending ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <FaPaperPlane className="text-xs" />
                      <span>{isPriority ? 'Send Priority Request' : 'Send Message'}</span>
                    </>
                  )}
                </motion.button>

                {/* Direct email fallback */}
                <p className="text-center text-xs text-gray-400">
                  Or email us directly at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-500 hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SupportWidget;
